/**
 * Node Types React Query Hook
 *
 * Fetches node type specifications from the API, including:
 * - Input/output field definitions
 * - Config field definitions for UI rendering
 */

import { useQuery } from '@tanstack/react-query';
import { imageWorkflowsApi } from '@/lib/api/imageWorkflows';
import type { ConfigFieldDefinition } from '@/types/workflowFields';
import type { NodeType } from '@/types/imageWorkflow';

// ============================================================================
// Query Keys
// ============================================================================

export const nodeTypeKeys = {
  all: ['node-types'] as const,
  list: () => [...nodeTypeKeys.all, 'list'] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch all node type specifications from the API
 *
 * Returns cached data with 5 minute stale time since node types rarely change.
 */
export function useNodeTypesQuery() {
  return useQuery({
    queryKey: nodeTypeKeys.list(),
    queryFn: () => imageWorkflowsApi.getNodeTypes(),
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
  });
}

/**
 * Hook to get node type spec for a specific node type
 */
export function useNodeTypeSpec(nodeType: NodeType | string | null) {
  const { data, isLoading, error } = useNodeTypesQuery();

  const spec = nodeType
    ? data?.node_types.find((t) => t.node_type === nodeType)
    : undefined;

  return {
    spec,
    isLoading,
    error,
  };
}

/**
 * Hook to get config fields for a specific node type
 *
 * Returns the config_fields array for the given node type,
 * which can be used for data-driven UI rendering.
 */
export function useNodeConfigFields(nodeType: NodeType | string | null) {
  const { spec, isLoading, error } = useNodeTypeSpec(nodeType);

  return {
    configFields: spec?.config_fields ?? [],
    isLoading,
    error,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if a config field should be visible based on show_when condition
 */
export function isConfigFieldVisible(
  field: ConfigFieldDefinition,
  nodeData: Record<string, unknown>
): boolean {
  if (!field.show_when) return true;

  const { field: dependField, value } = field.show_when;
  const currentValue = nodeData[dependField];

  if (Array.isArray(value)) {
    return value.includes(currentValue as string);
  }

  return currentValue === value;
}

/**
 * Get visible config fields based on current node data
 */
export function getVisibleConfigFields(
  configFields: ConfigFieldDefinition[],
  nodeData: Record<string, unknown>
): ConfigFieldDefinition[] {
  return configFields.filter((field) => isConfigFieldVisible(field, nodeData));
}
